// src/pages/admin/fees/FeePlanForm.jsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPlan } from "../../../api/fees";
import { fetchBranches } from "../../../api/branches";
import { useNavigate, useLocation } from "react-router-dom";
import InfoIcon from "../../../components/common/InfoIcon";
import PageHeaderSection from "../../../components/layout/PageHeaderSection";
import Button from "../../../components/common/Button";
import { TextInput, SelectInput, TextareaInput, CheckboxField } from "../../../components/common/form";

const PLAN_TYPES = ["PACKAGE", "MONTHLY", "PER_SESSION"];
const GROUP_TYPES = ["GROUP", "ONE_TO_ONE"];

const empty = {
    branchId: "",
    code: "",
    planType: "PACKAGE",
    groupType: "GROUP",
    defaultRate: "",
    defaultCredits: "",
    levelMin: "",
    levelMax: "",
    carryForwardAllowed: false,
    creditExpiryDays: "",
    active: true,
    notes: "",
};

export default function FeePlanForm() {
    const { t } = useTranslation();
    const [branches, setBranches] = useState([]);
    const [branchesLoadError, setBranchesLoadError] = useState(false);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const bid = params.get("branchId");
        if (bid) setForm((f) => ({ ...f, branchId: String(bid) }));
    }, [location.search]);

    useEffect(() => {
        (async () => {
            try {
                const res = await fetchBranches();
                const list = res?.data ?? [];
                const active = Array.isArray(list) ? list.filter((b) => b.active !== false) : [];
                setBranches(active);
                setBranchesLoadError(false);
            } catch (e) {
                console.error(e);
                setBranches([]);
                setBranchesLoadError(true);
            }
        })();
    }, []);

    const onChange = (e) => {
        const { name, type, checked, value } = e.target;
        setForm((f) => {
            const next = { ...f, [name]: type === "checkbox" ? checked : value };
            // If not GROUP => never store these flags
            if (name === "groupType" && value !== "GROUP") {
                next.carryForwardAllowed = false;
            }
            return next;
        });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.branchId) {
            alert(t("pages.feePlanForm.selectBranchAlert"));
            return;
        }
        if (!form.code?.trim()) {
            alert(t("pages.feePlanForm.codeRequired"));
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                branchId: Number(form.branchId),
                defaultRate: form.defaultRate !== "" ? Number(form.defaultRate) : null,
                defaultCredits: form.defaultCredits !== "" ? Number(form.defaultCredits) : null,
                levelMin: form.levelMin !== "" ? Number(form.levelMin) : null,
                levelMax: form.levelMax !== "" ? Number(form.levelMax) : null,
                creditExpiryDays: form.creditExpiryDays !== "" ? Number(form.creditExpiryDays) : null,
            };
            if (payload.groupType !== "GROUP") {
                payload.carryForwardAllowed = false;
            }
            await createPlan(payload);
            navigate("/admin/fees/plans");
        } catch (err) {
            console.error(err);
            alert(t("pages.feePlanForm.createFailed"));
        } finally {
            setSaving(false);
        }
    };

    const showGroupOnly = form.groupType === "GROUP";

    return (
        <div className="page-shell-form">
            <PageHeaderSection
                title={t("pages.feePlanForm.title")}
                subtitle={t("pages.feePlanForm.subtitle")}
            />

            <div className="form-shell">
                {branchesLoadError && (
                    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 md:col-span-3" role="status">
                        {t('common.optionsLoadFailed')}
                    </div>
                )}
                <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <SelectInput
                        label={t("pages.feePlanForm.branchLabel")}
                        name="branchId"
                        value={form.branchId}
                        onChange={onChange}
                        required
                        disabled={branchesLoadError}
                        note={branchesLoadError ? t('common.optionsLoadFailed') : undefined}
                    >
                        <option value="">{t("pages.feePlans.selectBranch")}</option>
                        {branches.map((b) => (
                            <option key={b.id} value={b.id}>
                                {b.name} (#{b.id})
                            </option>
                        ))}
                    </SelectInput>

                    <TextInput
                        label={t("pages.feePlanForm.codeLabel")}
                        name="code"
                        value={form.code}
                        onChange={onChange}
                        required
                    />

                    <SelectInput
                        label={t("pages.feePlanForm.typeLabel")}
                        name="planType"
                        value={form.planType}
                        onChange={onChange}
                    >
                        {PLAN_TYPES.map((p) => (
                            <option key={p} value={p}>
                                {t(`pages.feePlanForm.planTypes.${p}`, { defaultValue: p.replace(/_/g, ' ') })}
                            </option>
                        ))}
                    </SelectInput>

                    <SelectInput
                        label={t("pages.feePlanForm.groupTypeLabel")}
                        name="groupType"
                        value={form.groupType}
                        onChange={onChange}
                    >
                        {GROUP_TYPES.map((p) => (
                            <option key={p} value={p}>
                                {t(`pages.feePlanForm.groupTypes.${p}`, { defaultValue: p.replace(/_/g, ' ') })}
                            </option>
                        ))}
                    </SelectInput>

                    <TextInput
                        label={t("pages.feePlanForm.defaultRate")}
                        name="defaultRate"
                        type="number"
                        value={form.defaultRate}
                        onChange={onChange}
                    />
                    <TextInput
                        label={t("pages.feePlanForm.defaultCredits")}
                        name="defaultCredits"
                        type="number"
                        value={form.defaultCredits}
                        onChange={onChange}
                    />

                    <TextInput
                        label={t("pages.feePlanForm.levelMin")}
                        name="levelMin"
                        type="number"
                        value={form.levelMin}
                        onChange={onChange}
                    />
                    <TextInput
                        label={t("pages.feePlanForm.levelMax")}
                        name="levelMax"
                        type="number"
                        value={form.levelMax}
                        onChange={onChange}
                    />

                    {showGroupOnly && (
                        <div className="flex items-center gap-2">
                            <CheckboxField
                                name="carryForwardAllowed"
                                label={t("pages.feePlanForm.carryForward")}
                                checked={form.carryForwardAllowed}
                                onChange={onChange}
                            />
                            <InfoIcon
                                message={
                                    <>
                                        <div>{t("pages.feePlanForm.carryForwardEnabled")}</div>
                                        <div style={{ marginTop: 6 }}>
                                            {t("pages.feePlanForm.carryForwardDisabled")}
                                        </div>
                                    </>
                                }
                                docsUrl="/docs/fees#carry-forward"
                            />
                        </div>
                    )}

                    <TextInput
                        label={t("pages.feePlanForm.creditExpiry")}
                        name="creditExpiryDays"
                        type="number"
                        value={form.creditExpiryDays}
                        onChange={onChange}
                    />

                    <div className="md:col-span-3">
                        <TextareaInput
                            label={t("pages.feePlanForm.notes")}
                            name="notes"
                            rows={3}
                            value={form.notes}
                            onChange={onChange}
                        />
                    </div>

                    <CheckboxField
                        layout="toggle-card"
                        name="active"
                        label={t("pages.feePlanForm.active")}
                        checked={!!form.active}
                        onChange={onChange}
                    />

                    <div className="md:col-span-3 flex items-center justify-end gap-2 mt-2">
                        <Button type="button" variant="secondary" onClick={() => navigate('/admin/fees/plans')}>
                            {t("common.cancel")}
                        </Button>
                        <Button type="submit" variant="primary" disabled={saving}>
                            {saving ? t("pages.feePlanForm.saving") : t("pages.feePlanForm.createPlan")}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
